/** SUNTRONIX ONE — Quick Quote Launcher.
 *  One-click entry to the existing quotation composer (NewQuoteDrawer) plus a
 *  Recent Quotes strip. REUSES the quote engine via deep-links — no new engine.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileDown, Copy, ArrowUpRight, Zap, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STAGE_TONE = {
  DRAFT: "bg-slate-100 text-slate-700 border-slate-200",
  INTERNAL_REVIEW: "bg-amber-100 text-amber-800 border-amber-200",
  SENT: "bg-sky-100 text-sky-800 border-sky-200",
  ACCEPTED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  REJECTED: "bg-rose-100 text-rose-800 border-rose-200",
};
const money = (n, c = "INR") =>
  n == null ? "—" : `${c === "INR" ? "\u20B9" : c + " "}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function QuickQuoteLauncher({ compact = false }) {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/vtiger/quotations", { params: { limit: 8 } })
      .then(({ data }) => setRows((data.items || []).slice(0, 6)))
      .catch(() => setRows([]));
  }, []);

  const startQuote = useCallback(() => nav("/commercial/crm/quotations?new=1"), [nav]);
  const openQuote = useCallback((q) => nav(`/commercial/crm/quotations?open=${q.code || q.id}`), [nav]);
  const duplicate = useCallback((q) => {
    try {
      const items = (q.items || []).map((it) => ({
        line_type: it.line_type || "PRODUCT",
        product_id: it.product_id || null, product_code: it.product_code || null,
        part_number: it.part_number || null, product_name: it.product_name || "",
        description: it.description || "", sku: it.sku || null,
        quantity: it.quantity ?? 1, unit_price_inr: it.unit_price_inr ?? 0,
        vendor_unit_cost_inr: it.vendor_unit_cost_inr ?? 0,
        tax_pct: it.tax_pct ?? 18, discount_pct: it.discount_pct ?? 0,
      }));
      sessionStorage.setItem("st:quote-prefill", JSON.stringify({ items, subject: q.subject || "" }));
      const cid = q.customer_account_id || q.customer_master_company_id;
      nav(`/commercial/crm/quotations?new=1&prefill=1${cid ? `&customer_id=${cid}` : ""}`);
    } catch { toast.error("Could not duplicate this quote"); }
  }, [nav]);

  return (
    <div
      data-testid="quick-quote-launcher"
      className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-white p-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-indigo-600 font-semibold">
            <Zap className="h-3.5 w-3.5" /> Quick Quote
          </div>
          <h2 className={`font-heading font-semibold text-slate-900 mt-1 ${compact ? "text-xl" : "text-2xl"}`}>
            Prepare a quotation in under a minute.
          </h2>
          <p className="text-[13px] text-slate-600 mt-1 max-w-xl">
            Pick a customer, add products, and preview or send — everything is prefilled from your masters.
          </p>
        </div>
        <button
          onClick={startQuote}
          data-testid="quick-quote-start"
          className="h-12 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-[15px] font-semibold flex items-center gap-2 shadow-md transition-colors"
        >
          <Plus className="h-5 w-5" strokeWidth={2.4} /> Quick Quote
        </button>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Recent quotes</div>
          <button
            onClick={() => nav("/commercial/crm/quotations")}
            data-testid="quick-quote-view-all"
            className="text-[11px] font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
          >
            View all <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>

        {rows === null && (
          <div className="flex items-center gap-2 text-[12px] text-slate-400 py-4" data-testid="quick-quote-loading">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading recent quotes…
          </div>
        )}
        {rows && rows.length === 0 && (
          <div className="text-[12px] text-slate-500 py-4" data-testid="quick-quote-empty">
            No quotes yet — tap Quick Quote to create your first.
          </div>
        )}
        {rows && rows.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {rows.map((q) => (
              <div
                key={q.id}
                data-testid={`quick-quote-recent-${q.id}`}
                className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => openQuote(q)} className="flex items-center gap-2 min-w-0 text-left">
                    <span className="font-mono text-[12px] font-semibold text-slate-900">{q.code}</span>
                    <Badge className={`text-[9px] border ${STAGE_TONE[q.status] || "bg-slate-100 text-slate-700"}`}>{q.status}</Badge>
                  </button>
                  <span className="font-mono text-[12px] font-semibold text-slate-900 shrink-0">{money(q.grand_total_inr, q.currency)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1.5">
                  <button onClick={() => openQuote(q)} className="text-[12px] text-slate-600 truncate text-left flex-1 min-w-0 hover:text-slate-900">
                    {q.customer_name || q.customer_account_name || "—"}
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openQuote(q)}
                      data-testid={`quick-quote-open-${q.id}`}
                      title="Open / revise"
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400 transition-colors"
                    >
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </button>
                    <a
                      href={`${BACKEND}/api/vtiger/quotations/${q.id}/pdf/preview`}
                      target="_blank" rel="noreferrer"
                      data-testid={`quick-quote-pdf-${q.id}`}
                      title="Preview PDF"
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400 transition-colors"
                    >
                      <FileDown className="h-3.5 w-3.5" />
                    </a>
                    <button
                      onClick={() => duplicate(q)}
                      data-testid={`quick-quote-duplicate-${q.id}`}
                      title="Duplicate"
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400 transition-colors"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
